import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { DashboardHeader } from "@/components/DashboardHeader";
import { DashboardFooter } from "@/components/DashboardFooter";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { WelcomeSection } from "@/components/dashboard/WelcomeSection";
import { ProcessSteps } from "@/components/dashboard/ProcessSteps";
import { Clock, TrendingUp } from "lucide-react";

const UserDashboard = () => {
  const { data: latestCalculation } = useQuery({
    queryKey: ['latestCalculation'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('calculations')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      if (error) {
        console.error('Error fetching calculation:', error);
        return null;
      }
      return data;
    }
  });

  const savingsMessage = latestCalculation 
    ? `That's R${latestCalculation.monthly_savings.toFixed(2)} more in your pocket every month!` 
    : "Calculate your savings now to see how much more you could have in your pocket!";

  return (
    <div className="dark">
      <SidebarProvider defaultOpen={true}>
        <div className="flex flex-col min-h-screen bg-[#1A1F2C] dark:bg-[#1A1F2C] w-full">
          <DashboardHeader />
          <div className="flex flex-1 relative">
            <DashboardSidebar />
            <div className="flex-1 ml-64"> {/* Added margin-left to account for sidebar width */}
              <div className="absolute inset-0 bg-[url('/grid.svg')] bg-center [mask-image:linear-gradient(180deg,white,rgba(255,255,255,0))] dark:opacity-20 opacity-10" />
              <div className="relative px-6 py-8 lg:px-8">
                <div className="mx-auto max-w-4xl">
                  <div className="flex justify-between items-center mb-8">
                    <h1 className="text-3xl font-bold text-white">Overview</h1>
                    <SidebarTrigger />
                  </div>
                  
                  <div className="space-y-6">
                    <WelcomeSection 
                      latestCalculation={latestCalculation}
                      savingsMessage={savingsMessage}
                    />
                    <ProcessSteps />

                    {/* Step 3 */}
                    <div className="p-6 rounded-lg bg-[#222222]/50 border border-white/10">
                      <div className="flex items-start space-x-4">
                        <div className="p-3 bg-primary/20 rounded-lg">
                          <Clock className="w-6 h-6 text-primary" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-white mb-2">Step 3: Fast-Track Your Application</h3>
                          <p className="text-gray-300">
                            Complete your application within 24 hours to qualify for our fast-track processing and receive
                            an additional 5% reduction in your monthly payments. Our expert team will begin working on
                            your case immediately upon submission.
                          </p>
                          <div className="mt-4 p-4 bg-primary/10 rounded-lg border border-primary/20">
                            <p className="text-sm font-medium text-primary">
                              ⚡ Quick Submission Bonus: Additional 5% Reduction
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Savings Breakdown */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="p-6 rounded-lg bg-[#222222]/50 border border-white/10">
                        <div className="flex items-start space-x-4">
                          <div className="p-3 bg-green-500/20 rounded-lg">
                            <Clock className="w-6 h-6 text-green-500" />
                          </div>
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-white mb-2">Urgent Submission Bonus</h3>
                            <p className="text-gray-300">
                              Submit all documents within 24 hours to receive an additional 5% reduction,
                              bringing your total savings to 65%!
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="p-6 rounded-lg bg-[#222222]/50 border border-white/10">
                        <div className="flex items-start space-x-4">
                          <div className="p-3 bg-blue-500/20 rounded-lg">
                            <TrendingUp className="w-6 h-6 text-blue-500" />
                          </div>
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-white mb-2">Extra Savings Potential</h3>
                            <p className="text-gray-300">
                              Contact our support team to explore additional savings opportunities
                              through our premium services and partnerships.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <DashboardFooter />
        </div>
      </SidebarProvider>
    </div>
  );
};

export default UserDashboard;